import React, { createContext, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, {
  authAPI,
  persistAuthTokens,
  clearAuthTokens,
} from "@/lib/api";
import GoogleSignIn from "./GoogleSignIn";
const Session = createContext(null);
export const useSession = () => useContext(Session);
export function SessionProvider({ children }) {
  const [draft, setDraft] = useState(null);
  const [financialDraft, setFinancialDraft] = useState(null);
  const [ownershipContext, setOwnershipContext] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setFinancialDraft(null);
  }, [user?.id]);
  useEffect(() => {
    if (!localStorage.getItem("token")) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        const profileResponse = await authAPI.getProfile();
        setProfile(profileResponse.data);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);
  return (
    <Session.Provider
      value={{
        user,
        setUser,
        profile,
        setProfile,
        loading,
        draft,
        setDraft,
        financialDraft,
        setFinancialDraft,
        ownershipContext,
        setOwnershipContext,
      }}
    >
      {children}
    </Session.Provider>
  );
}
export default function Account() {
  const { user, setUser, profile, setProfile, loading } = useSession();
  const navigate = useNavigate();
  const [signup, setSignup] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(e.currentTarget);
    try {
      const body = { email: form.get("email"), password: form.get("password") };
      if (signup) body.name = form.get("name");
      const response = await api.post(
        signup ? "/auth/register" : "/auth/login",
        body,
      );
      persistAuthTokens(response.data);
      const signedInUser =
        response.data.user || (await api.get("/auth/me")).data;
      setUser(signedInUser);
      const profileResponse = await authAPI.getProfile();
      setProfile(profileResponse.data);
      if (signup || !profileResponse.data.onboarding_completed) {
        navigate("/onboarding");
      }
    } catch (err) {
      setError(
        "Unable to sign in or create the account. Check your details and try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function logout() {
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/logout-all");
      clearAuthTokens();
      setUser(null);
      setProfile(null);
    } catch (e) {
      setError(
        "Server sign-out failed. Local credentials have been cleared; existing sessions may remain active.",
      );
      clearAuthTokens();
      setUser(null);
      setProfile(null);
    } finally {
      setBusy(false);
    }
  }
  if (loading) return <p>Checking your session…</p>;
  return (
    <section className="research-card">
      <h1>
        {user ? "Your account" : signup ? "Create your account" : "Sign in"}
      </h1>
      {user ? (
        <>
          <p>Signed in as {user.email}</p>
          <p>
            <Link to="/research">Return to your research</Link>
          </p>
          <p>
            <Link to="/onboarding">
              {profile?.onboarding_completed
                ? "Review your buying preferences"
                : "Tell us what you are planning"}
            </Link>
          </p>
          <button onClick={logout} disabled={busy}>
            Sign out of all sessions
          </button>
        </>
      ) : (
        <>
          <p>
            Research without an account. Sign in when you want to save and
            compare reports.
          </p>
          <GoogleSignIn
            onSignedIn={async (signedInUser) => {
              setUser(signedInUser);
              const profileResponse = await authAPI.getProfile();
              setProfile(profileResponse.data);
              navigate(
                profileResponse.data.onboarding_completed
                  ? "/account"
                  : "/onboarding",
              );
            }}
            disabled={busy}
          />
          <form onSubmit={submit}>
            {signup && (
              <label>
                Name
                <input
                  name="name"
                  required
                  autoComplete="name"
                  maxLength={100}
                />
              </label>
            )}
            <label>
              Email
              <input name="email" type="email" required autoComplete="email" />
            </label>
            <label>
              Password
              <input
                name="password"
                type="password"
                required
                minLength={signup ? 12 : 1}
                maxLength={72}
                autoComplete={signup ? "new-password" : "current-password"}
              />
            </label>
            <button disabled={busy}>
              {busy ? "Please wait…" : signup ? "Create account" : "Sign in"}
            </button>
          </form>
          <button
            className="secondary"
            onClick={() => {
              setSignup(!signup);
              setError("");
            }}
          >
            {signup ? "Already have an account? Sign in" : "Create an account"}
          </button>
        </>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
